import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportContext } from '../../context/SupportContext';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from '../../utils/i18n';
import {
  Eye,
  ArrowLeft,
  Lock,
  User,
  Building2,
  Shield,
} from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Support Mode Banner - Phase 9.1 (Brand Unified)
 * 
 * Premium glassmorphism design with UNIFIED FundElite branding.
 * Context differences are subtle - NOT full color identity shifts.
 * 
 * Visual States:
 * - All support modes use the same FundElite emerald-based styling
 * - Context type shown via badges/labels only
 * - Read-only vs Interactive shown via subtle indicators
 */
const SupportModeBanner = () => {
  const navigate = useNavigate();
  const { supportContext, isInSupportMode, exitSupportMode, isLoading } = useSupportContext();
  const { language } = useTheme();
  const { t } = useTranslation(language);

  const handleExit = async () => {
    await exitSupportMode();
    navigate('/platform/dashboard');
  };

  if (!isInSupportMode) return null;

  // Determine context type for labels/badges (NOT for colors)
  const isReadOnly = supportContext?.is_read_only;
  const isClientView = supportContext?.effective_user_id && 
                       supportContext?.effective_user_id !== supportContext?.real_actor_user_id;
  const portalType = supportContext?.effective_portal_type;
  
  // Determine context label
  const getContextLabel = () => {
    if (isClientView) {
      if (portalType === 'managed_client_portal') {
        return { label: t('context.managed'), icon: User };
      }
      return { label: t('context.selfServe'), icon: User };
    }
    return { label: t('context.agencyAdmin'), icon: Building2 };
  };

  const contextInfo = getContextLabel();
  const ContextIcon = contextInfo.icon;

  return (
    <div 
      className="sticky top-0 z-50"
      data-testid="support-mode-banner"
    >
      {/* Main Banner - Unified FundElite Glassmorphism */}
      <div 
        className="border-b"
        style={{
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(15, 23, 42, 0.95) 50%, rgba(16, 185, 129, 0.08) 100%)',
          borderColor: 'rgba(16, 185, 129, 0.2)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-2.5">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Mode indicator */}
            <div className="flex items-center gap-4">
              {/* Animated icon */}
              <div className="relative">
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center border"
                  style={{
                    background: 'rgba(16, 185, 129, 0.15)',
                    borderColor: 'rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <Eye size={18} className="text-emerald-400" />
                </div>
                {/* Pulse ring */}
                <div 
                  className="absolute inset-0 rounded-xl animate-ping opacity-20"
                  style={{ 
                    background: 'rgba(16, 185, 129, 0.3)',
                    animationDuration: '2s' 
                  }} 
                />
              </div>
              
              {/* Context info */}
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-100 font-medium text-sm">
                      {t('support.mode')}
                    </span>
                    {/* Context badge - subtle, unified styling */}
                    <span 
                      className="px-2 py-0.5 text-xs font-medium rounded-full border"
                      style={{
                        background: 'rgba(16, 185, 129, 0.2)',
                        borderColor: 'rgba(16, 185, 129, 0.4)',
                        color: '#6ee7b7',
                      }}
                    >
                      {contextInfo.label}
                    </span>
                    {isReadOnly && (
                      <span 
                        className="px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1 border"
                        style={{
                          background: 'rgba(148, 163, 184, 0.15)',
                          borderColor: 'rgba(148, 163, 184, 0.3)',
                          color: '#cbd5e1',
                        }}
                      >
                        <Lock size={10} />
                        {t('support.readOnly')}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-emerald-300/80 mt-0.5 flex items-center gap-1.5">
                    {isClientView ? (
                      <>
                        <User size={12} />
                        <span>
                          {t('support.viewingAs')} <strong className="font-semibold text-emerald-200">{supportContext?.effective_user_name || 'Client'}</strong>
                        </span>
                      </>
                    ) : (
                      <>
                        <Building2 size={12} />
                        <span>
                          {t('support.agency')}: <strong className="font-semibold text-emerald-200">{supportContext?.effective_agency_name || supportContext?.effective_agency_id}</strong>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Exit action */}
            <Button
              onClick={handleExit}
              disabled={isLoading}
              size="sm"
              className="border-0 shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'rgba(16, 185, 129, 0.2)',
                color: '#6ee7b7',
                backdropFilter: 'blur(8px)',
              }}
              data-testid="exit-support-btn"
            >
              <ArrowLeft size={16} className="mr-2" />
              <span className="hidden sm:inline">{t('support.returnToPlatform')}</span>
              <span className="sm:hidden">Exit</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Subtle bottom glow line - Brand emerald */}
      <div 
        className="h-px opacity-40"
        style={{ 
          background: 'linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.5), transparent)',
        }}
      />
    </div>
  );
};

export default SupportModeBanner;
